import { useEffect, useState } from 'react';
import { useAgentApi } from '../Services/AgentApiProvider';
import { AssistantInstructionApi } from '../Services/AgentApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';
import AgentInstructionEdit from './AgentInstructionEdit';
import { useAlert } from './AlertContext';
import Button from './Buttons';
import { useConfirmation } from './ConfirmationContext';
import SearchInput from './SearchInput';
import './SearchInput.css';
import Table from './Tables';

interface AgentInstructionsListProps {
  agentId: string;
  canEdit: boolean;
  agentInstructions: AssistantInstructionApi[];
  onUpdateTrigger: () => void;
  onSearch: (searchTerm: string) => void;
  onUpdateList?: () => void;
  loading: boolean;
  totalInstructions: number;
  persistedPageSize: number;
  tableTitle: string;
  onChangePage: (skip: number, limit: number) => void;
}

function AgentInstructionsList({
  agentId,
  canEdit,
  agentInstructions = [],
  onUpdateTrigger,
  onSearch,
  onUpdateList,
  loading,
  totalInstructions,
  persistedPageSize,
  tableTitle,
  onChangePage,
}: AgentInstructionsListProps) {
  const { showConfirmation } = useConfirmation();
  const { showAlert } = useAlert();
  const { deleteAgentInstruction } = useAgentApi();

  const [editingInstruction, setEditingInstruction] = useState<AssistantInstructionApi | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(searchTerm);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (onSearch) {
      onSearch(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearch]);

  const columns = [
    {
      name: 'Text',
      selector: (row: AssistantInstructionApi) => {
        const original_text = row.original_text ?? '';
        const limit = 60;

        return original_text.length > limit
          ? `${original_text.substring(0, limit)}...`
          : original_text;
      },
      sortable: false,
    },
    {
      name: 'Instruction',
      selector: (row: AssistantInstructionApi) => {
        const instructionText = row.instruction ?? '';
        const limit = 60;

        return instructionText.length > limit
          ? `${instructionText.substring(0, limit)}...`
          : instructionText;
      },
      sortable: false,
    },
    {
      name: 'Status',
      selector: (row: AssistantInstructionApi) => (row.is_active ? 'Active' : 'InActive'),
      sortable: false,
    },
    {
      name: 'Updated',
      selector: (row: AssistantInstructionApi) => formatDateUTC(row.updated_at),
      sortable: false,
    },
    {
      name: 'Created',
      selector: (row: AssistantInstructionApi) => formatDateUTC(row.created_at),
      sortable: false,
    },
  ];

  const handleEditClick = (row: AssistantInstructionApi) => {
    setEditingInstruction(row);
  };

  const handleCreateClick = (): void => {
    setEditingInstruction({
      id: '',
      instruction: '',
      original_text: '',
      is_active: false,
      created_at: '',
      updated_at: '',
    });
  };

  const handleDelete = (selectedId: string[]): void => {
    if (!selectedId[0] || !canEdit) return;
    showConfirmation('Are you sure you want to delete this instruction?', async () => {
      try {
        await deleteAgentInstruction(agentId, selectedId[0]);
        if (onUpdateList) onUpdateList();
        onUpdateTrigger();
      } catch (error) {
        showAlert('fail', 'Failed to delete the instruction.');
      }
    });
  };

  return (
    <div className="radius-yes p-5 bg-yes col-12">
      {editingInstruction ? (
        <AgentInstructionEdit
          agentId={agentId}
          initialData={editingInstruction}
          canEdit={canEdit}
          onClose={() => setEditingInstruction(null)}
          onUpdate={() => {
            setEditingInstruction(null);
            if (onUpdateList) onUpdateList();
          }}
          onUpdateTrigger={onUpdateTrigger}
        />
      ) : (
        <>
          <h2 className="mb-4">
            <SearchInput
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder="Search instructions..."
            />
          </h2>
          <Table
            title={tableTitle}
            columns={columns}
            data={agentInstructions}
            onRowClicked={handleEditClick}
            onDelete={handleDelete}
            selectableRows={canEdit}
            selectableRowsHighlight
            pagination
            progressPending={loading}
            paginationTotalRows={totalInstructions}
            paginationPerPage={persistedPageSize}
            onChangePage={onChangePage}
          />
          {canEdit && (
            <div className="d-flex justify-start" style={{ marginTop: '1rem' }}>
              <Button size="large" onClick={handleCreateClick}>
                Create New Instruction
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AgentInstructionsList;
